import React, { useEffect, useState, useCallback } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Trash2, Copy, Loader2, ImageIcon, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const absUrl = (u) => (u && u.startsWith('/') ? `${BACKEND_URL}${u}` : u);

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (s) => {
  try { return new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return ''; }
};

export default function MediaLibraryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/media-library`, { withCredentials: true });
      setItems(res.data.items || []);
    } catch (e) {
      toast.error('Failed to load media library');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) continue;
        const formData = new FormData();
        formData.append('file', file);
        await axios.post(`${API}/media-library/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        });
      }
      toast.success('Uploaded');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Remove "${item.original_filename || 'image'}" from your library?`)) return;
    try {
      await axios.delete(`${API}/media-library/${item.id}`, { withCredentials: true });
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      toast.success('Removed');
    } catch (e) {
      toast.error('Failed to remove image');
    }
  };

  const handleCopy = async (item) => {
    try {
      await navigator.clipboard.writeText(absUrl(item.url));
      toast.success('Image URL copied');
    } catch {
      toast.error('Could not copy URL');
    }
  };

  const filtered = items.filter((i) =>
    !search.trim() || (i.original_filename || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-[#F4E4C1] to-[#E8D5B7] py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
            <div>
              <h1 className="text-4xl retro-title text-[#6B4423]">Media Library</h1>
              <p className="text-[#6B4423]/80 text-sm mt-1">All images uploaded across your site — stored permanently.</p>
            </div>
            <div>
              <input id="media-lib-upload" type="file" accept="image/*" multiple onChange={handleUpload} disabled={uploading} className="hidden" data-testid="media-lib-upload-input" />
              <button
                onClick={() => document.getElementById('media-lib-upload').click()}
                disabled={uploading}
                data-testid="media-lib-upload-button"
                className="retro-button flex items-center gap-2 bg-[#1B4B5A] hover:bg-[#143a45] text-white disabled:opacity-60"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? 'Uploading…' : 'Upload Images'}
              </button>
            </div>
          </div>

          <div className="admin-card">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <h2 className="text-2xl font-bold text-white">{filtered.length} image{filtered.length === 1 ? '' : 's'}</h2>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by filename…"
                  data-testid="media-lib-search"
                  className="pl-9 pr-4 py-2 border-2 border-[#8B6F47] rounded focus:outline-none focus:border-[#6B4423] text-gray-900 bg-white"
                />
              </div>
            </div>

            {loading ? (
              <div className="text-center py-16"><Loader2 className="w-8 h-8 text-white animate-spin mx-auto" /></div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 text-white/80">
                <ImageIcon className="w-12 h-12 mx-auto mb-3 opacity-60" />
                {items.length === 0 ? 'No images yet. Upload some to get started.' : 'No images match your search.'}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="media-lib-grid">
                {filtered.map((item) => (
                  <div key={item.id} className="bg-white rounded-lg overflow-hidden border-2 border-[#8B6F47]" data-testid={`media-lib-item-${item.id}`}>
                    <div className="aspect-square bg-gray-100">
                      <img src={absUrl(item.url)} alt={item.original_filename} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-3">
                      <p className="text-xs font-semibold text-[#6B4423] truncate" title={item.original_filename}>{item.original_filename}</p>
                      <p className="text-[10px] text-gray-500 mt-0.5">{formatSize(item.size)} · {formatDate(item.created_at)}</p>
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => handleCopy(item)} data-testid={`media-lib-copy-${item.id}`} className="flex-1 inline-flex items-center justify-center gap-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded py-1.5" title="Copy URL">
                          <Copy className="w-3.5 h-3.5" /> Copy
                        </button>
                        <button onClick={() => handleDelete(item)} data-testid={`media-lib-delete-${item.id}`} className="inline-flex items-center justify-center bg-red-50 hover:bg-red-100 text-red-600 rounded py-1.5 px-2.5" title="Delete">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
